import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building, 
  MapPin, 
  Phone, 
  Calendar, 
  ArrowLeft, 
  Users,
  Settings,
  User,
  Sprout
} from 'lucide-react';
import { toast } from '@etherisc/ui-kit';
import { PageContent } from '../../components/layout/PageContent';
import { NeutralButton, PositiveButton } from '../../components/buttons';
import { organizationService } from '../../services/organizationService';
import type { Organization, ContactPerson, AffiliatedUser, AssociatedFarmer } from '../../types/organization';
import { ORGANIZATION_TYPE_LABELS } from '../../types/organization';
import { formatDate } from '../../utils/formatters';

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadOrganization(id);
    }
  }, [id]);

  const loadOrganization = async (organizationId: string) => {
    try {
      setLoading(true);
      const response = await organizationService.getOrganizationById(organizationId);
      
      // Validate the organization data structure
      if (!response) {
        throw new Error('No organization data received');
      }
      
      setOrganization(response);
    } catch (error) {
      console.error('Error loading organization:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load organization details';
      toast.error(`Error: ${errorMessage}`);
      navigate('/admin/organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/organizations');
  };

  const handleEdit = () => {
    if (organization) {
      navigate(`/admin/organizations/${organization.id}/edit`);
    }
  };

  const renderContactPerson = () => {
    const contactPerson = organization?.settings?.contact_person as ContactPerson | undefined;
    
    if (!contactPerson) {
      return <p className="text-gray-500">No contact person configured</p>;
    }

    return (
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Name:</span>
          <span className="font-medium">{contactPerson.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Role:</span>
          <span className="font-medium">{contactPerson.role}</span>
        </div>
        {contactPerson.email && (
          <div className="flex justify-between">
            <span className="text-gray-600">Email:</span>
            <span className="font-medium">{contactPerson.email}</span>
          </div>
        )}
        {contactPerson.contactNumber && (
          <div className="flex justify-between">
            <span className="text-gray-600">Phone:</span>
            <span className="font-medium">{contactPerson.contactNumber}</span>
          </div>
        )}
      </div>
    );
  };

  const renderTIN = () => {
    const tin = (organization?.settings as { tin?: string })?.tin;
    
    if (!tin) {
      return <p className="text-gray-500">No TIN configured</p>;
    }

    return (
      <div className="flex justify-between">
        <span className="text-gray-600">TIN:</span>
        <span className="font-medium">{tin}</span>
      </div>
    );
  };

  const renderAddress = () => {
    if (!organization) return null;

    const addressParts = [
      organization.addressLine1,
      organization.addressLine2,
      organization.city,
      organization.stateProvince,
      organization.postalCode,
      organization.country
    ].filter(Boolean);

    if (addressParts.length === 0) {
      return <p className="text-gray-500">No address configured</p>;
    }

    return (
      <div className="space-y-1">
        {organization.addressLine1 && <p>{organization.addressLine1}</p>}
        {organization.addressLine2 && <p>{organization.addressLine2}</p>}
        <p>
          {[organization.city, organization.stateProvince].filter(Boolean).join(', ')}
          {organization.postalCode && ` ${organization.postalCode}`}
        </p>
        {organization.country && <p>{organization.country}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <PageContent title="Loading..." description="Loading organization details...">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </PageContent>
    );
  }

  if (!organization) {
    return (
      <PageContent title="Organisation Not Found" description="The requested organisation could not be found.">
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">The organisation you're looking for doesn't exist or you don't have permission to view it.</p>
          <NeutralButton onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Organisations
          </NeutralButton>
        </div>
      </PageContent>
    );
  }

  // Additional safety check for organization data integrity
  if (!organization.id || !organization.name) {
    return (
      <PageContent title="Invalid Organisation Data" description="The organisation data appears to be corrupted.">
        <div className="text-center py-12">
          <Building className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Invalid organisation data</h3>
          <p className="text-gray-600 mb-6">The organisation data is corrupted or incomplete. Please contact support.</p>
          <NeutralButton onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Organisations
          </NeutralButton>
        </div>
      </PageContent>
    );
  }

  const pageActions = (
    <div className="flex items-center space-x-3">
      <NeutralButton onClick={handleBack}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Organisations
      </NeutralButton>
      <PositiveButton onClick={handleEdit}>
        Edit Organisation
      </PositiveButton>
    </div>
  );

  return (
    <PageContent
      title={organization.name}
      description={`Organisation details for ${organization.name} (${organization.code})`}
      actions={pageActions}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Basic Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Organisation Code:</span>
                <span className="font-medium font-mono">{organization.code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium">{ORGANIZATION_TYPE_LABELS[organization.type]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  organization.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {organization.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              {organization.userCount !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Users:</span>
                  <span className="font-medium flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {organization.userCount}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Contact Information
            </h3>
            <div className="space-y-3">
              {organization.phone ? (
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{organization.phone}</span>
                </div>
              ) : (
                <p className="text-gray-500">No phone number configured</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Address
            </h3>
            {renderAddress()}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Contact Person */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Contact Person
            </h3>
            {renderContactPerson()}
          </div>

          {/* Tax Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Tax Information
            </h3>
            {renderTIN()}
          </div>

          {/* System Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              System Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Created:</span>
                <span className="font-medium">{formatDate(organization.createdAt.toString())}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">{formatDate(organization.updatedAt.toString())}</span>
              </div>
              {organization.createdBy && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Created By:</span>
                  <span className="font-medium">{organization.createdBy}</span>
                </div>
              )}
              {organization.updatedBy && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Updated By:</span>
                  <span className="font-medium">{organization.updatedBy}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Affiliated Users Table */}
      <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Affiliated Users
        </h3>
        {organization.users && organization.users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Email</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organization.users.map((user: AffiliatedUser) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No affiliated users found</p>
        )}
      </div>

      {/* Associated Farmers Table */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Sprout className="w-5 h-5 mr-2" />
          Associated Farmers
        </h3>
        {organization.farmers && organization.farmers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Phone</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organization.farmers.map((farmer: AssociatedFarmer) => (
                  <tr key={farmer.id}>
                    <td>{farmer.name}</td>
                    <td>{farmer.phone}</td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        farmer.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {farmer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                        onClick={() => navigate(`/admin/farmers/${farmer.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No associated farmers found</p>
        )}
      </div>
    </PageContent>
  );
}
